import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import enUS from "./locales/en-US";
import zhCN from "./locales/zh-CN";

const LOCALE_STORAGE_KEY = "shoprises_locale";
const DEFAULT_LOCALE = "en-US";
const LOCALE_OPTIONS = ["zh-CN", "en-US"];

const dictionaries = {
  "zh-CN": zhCN,
  "en-US": enUS,
};

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key) => {
    throw new Error(`Missing I18nProvider for translation key: ${key}`);
  },
});

const isSupportedLocale = (locale) => LOCALE_OPTIONS.includes(locale);

const getBrowserLocale = () => {
  // Gatsby SSR 阶段没有 navigator，这里必须只在浏览器侧读取语言。
  if (typeof window === "undefined" || typeof window.navigator === "undefined") {
    return DEFAULT_LOCALE;
  }

  const browserLocale =
    window.navigator.languages && window.navigator.languages.length
      ? window.navigator.languages[0]
      : window.navigator.language;

  return String(browserLocale).toLowerCase().startsWith("zh")
    ? "zh-CN"
    : DEFAULT_LOCALE;
};

const getStoredLocale = () => {
  // localStorage 只在浏览器可用，SSR 需要回退到默认语言避免构建时报错。
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }

  const locale = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  return isSupportedLocale(locale) ? locale : null;
};

const getInitialLocale = () => getStoredLocale() || getBrowserLocale();

const getValueByPath = (source, path) =>
  path.split(".").reduce((result, key) => {
    if (result && Object.prototype.hasOwnProperty.call(result, key)) {
      return result[key];
    }
    return undefined;
  }, source);

const interpolate = (value, params) => {
  if (typeof value !== "string" || !params) {
    return value;
  }

  return Object.keys(params).reduce(
    (result, key) => result.replace(new RegExp(`{{\\s*${key}\\s*}}`, "g"), params[key]),
    value
  );
};

const createTranslator = (locale) => (key, params) => {
  const dictionary = dictionaries[locale];
  if (!dictionary) {
    throw new Error(`Missing dictionary for locale: ${locale}`);
  }

  const value = getValueByPath(dictionary, key);
  if (value === undefined) {
    throw new Error(`Missing translation for ${locale}: ${key}`);
  }
  return interpolate(value, params);
};

export const hasTranslation = (locale, key) =>
  Boolean(dictionaries[locale]) &&
  getValueByPath(dictionaries[locale], key) !== undefined;

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(DEFAULT_LOCALE);

  useEffect(() => {
    setLocaleState(getInitialLocale());
  }, []);

  const setLocale = (nextLocale) => {
    if (!isSupportedLocale(nextLocale)) {
      return;
    }

    setLocaleState(nextLocale);
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
    }
  };

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t: createTranslator(locale),
    }),
    [locale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);

export { LOCALE_OPTIONS };
